import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  getDocs,
  limit,
  setDoc
} from "firebase/firestore";
import { db } from "../firebase";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API;

export const chatService = {
  // 1-on-1 Chat check/creation
  async createChat(user1, user2) {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("isGroup", "==", false),
      where("participants", "array-contains", user1.id)
    );

    const querySnapshot = await getDocs(q);
    let existingChat = null;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.participants.includes(user2.id)) {
        existingChat = { id: doc.id, ...data };
      }
    });

    if (existingChat) return existingChat;

    const newChat = {
      isGroup: false,
      participants: [user1.id, user2.id],
      participantsData: {
        [user1.id]: { name: user1.name, avatar: user1.avatar || null, role: user1.role || 'user' },
        [user2.id]: { name: user2.name, avatar: user2.avatar || null, role: user2.role || 'user' }
      },
      lastMessage: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(chatsRef, newChat);
    return { id: docRef.id, ...newChat };
  },

  // Group Chat creation
  async createGroup(members, groupName) {
    const chatsRef = collection(db, "chats");
    const participants = members.map(m => m.id);
    const participantsData = {};
    members.forEach(m => {
      participantsData[m.id] = { name: m.name, avatar: m.avatar || null, role: m.role || 'user' };
    });

    const newGroup = {
      isGroup: true,
      groupName,
      participants,
      participantsData,
      lastMessage: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(chatsRef, newGroup);
    return { id: docRef.id, ...newGroup };
  },

  // Send Message
  async sendMessage(chatId, senderId, content, participants) {
    const messagesRef = collection(db, "chats", chatId, "messages");
    const newMessage = {
      senderId,
      content,
      timestamp: serverTimestamp(),
      readBy: [senderId]
    };

    await addDoc(messagesRef, newMessage);

    // Update parent chat doc
    const chatDocRef = doc(db, "chats", chatId);
    await updateDoc(chatDocRef, {
      lastMessage: {
        content,
        timestamp: new Date(), // using local date for immediate UI update, firestore will replace if using serverTimestamp in field
        senderId,
        readBy: [senderId]
      },
      updatedAt: serverTimestamp()
    });

    // Trigger Laravel (Node.js) Notifications
    try {
      await axios.post(`${API_BASE_URL}/chat-notification/send`, {
        chatId,
        senderId,
        content,
        participants: participants.filter(p => p.id !== senderId)
      });

    } catch (error) {
      console.error("Failed to send notification:", error);
    }
  },

  // Real-time listener for user's chat list
  subscribeToChats(userId, callback) {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", userId),
      orderBy("updatedAt", "desc")
    );

    return onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(chats);
    });
  },

  // Real-time listener for messages in a chat
  subscribeToMessages(chatId, callback) {
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(messages);
    });
  },

  // Mark chat as read
  async markAsRead(chatId, userId) {
    const chatDocRef = doc(db, "chats", chatId);
    
    // This is a bit simplified, ideally you'd mark individual messages
    // but for the metadata update:
    await updateDoc(chatDocRef, {
      "lastMessage.readBy": [userId] // This should really be an union, but keeping it simple for now
    });

    // Trigger backend to clear notifications
    try {
      await axios.post(`${API_BASE_URL}/chat-notification/read/${chatId}`);
    } catch (error) {
      console.error("Failed to clear notifications:", error);
    }
  },

  // Track Presence
  async updatePresence(userId, activeChatId) {
    const presenceRef = doc(db, "presence", userId.toString());
    await setDoc(presenceRef, {
      userId,
      activeChatId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  }
};
